/**
 * Servicio de áreas admin · funciones puras que tocan el API.
 *
 * Mantenemos el contrato del backend acá (DTOs in/out) para que los
 * hooks y componentes nunca importen directo de `lib/admin-api`. Esto
 * permite eliminar el archivo legacy cuando todas las features tengan
 * su propio servicio.
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { Area } from "@/lib/types";

export interface CrearAreaInput {
  nombre: string;
  descripcion: string | null;
}

export interface EditarAreaInput {
  nombre: string;
  descripcion: string | null;
  dispersionExcelHabilitada: boolean;
}

export interface ListarAreasParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Envelope paginado del backend + total del catálogo completo para el header. */
export interface PaginaAreas {
  items: Area[];
  total: number;
  page: number;
  pageSize: number;
  totalCatalogo: number;
}

export async function listarAreasAdmin(
  params: ListarAreasParams = {}
): Promise<PaginaAreas> {
  const { data } = await axiosClient.get<PaginaAreas>("/api/admin/areas", {
    params,
  });
  return data;
}

/**
 * Catálogo COMPLETO de áreas · loop de páginas (pageSize máx del backend).
 * Lo consumen las vistas que asignan sobre todo el catálogo (usuarios,
 * niveles de autorización), no sobre la página visible.
 */
export async function listarTodasLasAreas(): Promise<Area[]> {
  const todas: Area[] = [];
  for (let p = 1; ; p++) {
    const pagina = await listarAreasAdmin({ page: p, pageSize: 100 });
    todas.push(...pagina.items);
    if (todas.length >= pagina.total || pagina.items.length === 0) break;
  }
  return todas;
}

export async function crearArea(input: CrearAreaInput): Promise<Area> {
  const { data } = await axiosClient.post<Area>("/api/admin/areas", input);
  return data;
}

export async function editarArea(
  id: string,
  input: EditarAreaInput
): Promise<Area> {
  const { data } = await axiosClient.patch<Area>(`/api/admin/areas/${id}`, input);
  return data;
}
